/**
 * DocuSeal template configuration for the Broker MC Authority agreement.
 * Single source of truth for field names, roles, and types.
 *
 * Used by:
 * - Backend adapter/service to prefill field values
 * - Startup validation to verify the remote template matches expectations
 */

export const DOCUSEAL_ROLES = {
  MOTOR_CARRIER: 'Motor Carrier',
  TRANSPORTATION_BROKER: 'Transportation Broker',
} as const

export type DocuSealRole = (typeof DOCUSEAL_ROLES)[keyof typeof DOCUSEAL_ROLES]

export const DOCUSEAL_FIELDS = {
  // Motor Carrier (driver) fields
  CARRIER_NAME: { name: 'Carrier Name', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  CARRIER_ADDRESS: { name: 'Carrier Address', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  USDOT: { name: 'USDOT #', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  CARRIER_REPRESENTATIVE: {
    name: 'Representative Name',
    role: DOCUSEAL_ROLES.MOTOR_CARRIER,
    type: 'text',
  },
  CARRIER_MC_NUMBER: { name: 'MC #', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  /**
   * Prefilled from the `ein` column and from nothing else. The label says
   * "EIN/SSN" because the blank accepts either, but a driver's SSN lives
   * encrypted in `onboarding.drivers.ssn_encrypted` and is deliberately never
   * decrypted to fill a third-party document — a carrier with no EIN types
   * their own SSN at signing time.
   */
  CARRIER_EIN: { name: 'EIN/SSN', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  /**
   * Declared but never prefilled: the carrier ticks their own entity type in
   * DocuSeal, and nothing in the database records one. It is listed here purely
   * so `validateDocuSealTemplate` asserts it still exists and still belongs to
   * the Motor Carrier — assigned to the broker it would render read-only to the
   * one party who has to answer it, and nothing else would notice.
   */
  CARRIER_ENTITY_TYPE: {
    name: 'Type of company',
    role: DOCUSEAL_ROLES.MOTOR_CARRIER,
    type: 'multiple',
  },
  CARRIER_SIGNATURE: { name: 'Signature', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'signature' },
  CARRIER_DATE: { name: 'Date', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'date' },

  // Transportation Broker (Allied) fields
  /**
   * Opening "made and entered into this ___ day of ___" date.
   * Text (not date) field because DocuSeal's native date type doesn't
   * support the "13 day of April 2026" format. Prefilled at envelope
   * creation and marked read-only in the template.
   */
  AGREEMENT_DATE: {
    name: 'Agreement Date',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_NAME: {
    name: 'Broker Name',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_ADDRESS: {
    name: 'Broker Address',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_REPRESENTATIVE: {
    name: 'Representative Name',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_SIGNATURE: {
    name: 'Signature',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'signature',
  },
  BROKER_DATE: { name: 'Date', role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER, type: 'date' },
} as const

export type DocuSealFieldKey = keyof typeof DOCUSEAL_FIELDS
